import { memo, useMemo, useState } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { ContentSectionCustomCard, Image, Section } from "../../types";
import { CustomCard } from "./CustomCard";
import { FeatureCardContent } from "./FeatureCardContent";
import EmblaCarousel from "./EmblaCarousel";
import { EmblaOptionsType, EmblaCarouselType } from "embla-carousel";
import { ButtonCarousel } from "../carousel/ButtonCarousel";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";

const FEATURE_HERO_CARD_TYPE_3_2 = 13;

interface FeatureCardContainerProps {
  data: ContentSectionCustomCard[];
  scrollByCount?: number;
  isRoadblock?: boolean;
  section?: Section;
  sectionIndex?: number;
}

export const FeatureCardContainer = memo(
  ({
    data,
    isRoadblock = false,
    section,
    sectionIndex,
  }: FeatureCardContainerProps) => {
    const OPTIONS: EmblaOptionsType = useMemo(
      () => ({
        align: "start" as const,
        loop: false,
        watchDrag: data.length > 1,
        containScroll: "trimSnaps" as const,
        dragFree: false,
        skipSnaps: false,
        slidesToScroll: 1,
      }),
      [data.length],
    );

    const [selectedIndex, setSelectedIndex] = useState(0);
    const isMobile = useMobileBreakpoint();

    const handleSlideChange = (index: number) => {
      setSelectedIndex(index);
    };

    const handleClickHiddenCard = (
      clickedCardIndex: number,
      emblaApi: EmblaCarouselType | undefined,
    ) => {
      if (emblaApi) {
        if (clickedCardIndex < selectedIndex) {
          emblaApi.scrollPrev();
        } else if (clickedCardIndex > selectedIndex) {
          emblaApi.scrollNext();
        }
      }
    };

    const featureCardResponsiveWidths = {
      base: "260px",
      sm: "326px",
      md: "358px",
    };

    const featureCardResponsiveHeights = {
      base: "174px",
      sm: "218px",
      md: "239px",
    };

    const roadblockFeatureCardResponsiveWidths = {
      base: "332px",
      sm: "358px",
      lg: "472px",
      xl: "527px",
      "2xl": "632px",
    };

    const roadblockFeatureCardResponsiveHeights = {
      base: "221px",
      sm: "239px",
      lg: "315px",
      xl: "352px",
      "2xl": "421px",
    };

    const roadblockMobileHeight = "calc((100dvw - 8px) * 0.66)";

    const responsiveHeights = isRoadblock
      ? isMobile
        ? roadblockMobileHeight
        : roadblockFeatureCardResponsiveHeights
      : featureCardResponsiveHeights;

    const responsiveWidths = isRoadblock
      ? isMobile
        ? "calc(100% - 8px)"
        : roadblockFeatureCardResponsiveWidths
      : featureCardResponsiveWidths;

    const maxMobileWidth = "400px";
    const maxMobileHeight = "275px";

    const renderSlides = useMemo(
      () => (emblaApi: EmblaCarouselType | undefined) =>
        data.map((customCardData, index) => {
          const isHidden = isMobile && isRoadblock && selectedIndex !== index;

          // Lazy render: only render visible + adjacent cards
          const distance = Math.abs(index - selectedIndex);
          const shouldRender = distance <= 2; // Render visible + 2 adjacent

          if (!shouldRender && isMobile && isRoadblock) {
            // Return minimal placeholder for off-screen cards on mobile
            return (
              <Box
                key={customCardData.id}
                flex="0 0 100%"
                width="100%"
                paddingRight="16px"
              />
            );
          }

          return (
            <Flex
              direction={isRoadblock ? (isMobile ? "column" : "row") : "column"}
              key={customCardData.id}
              onClick={() => isHidden && handleClickHiddenCard(index, emblaApi)}
              style={
                isRoadblock
                  ? {
                      flex: "0 0 100%",
                      width: "100%",
                      paddingRight: "16px",
                      gap: !isMobile ? "32px" : undefined,
                      alignItems: isMobile ? "center" : undefined,
                      maxWidth: "1350px",
                      margin: "auto",
                    }
                  : undefined
              }
            >
              <CustomCard
                coverImage={getCoverImage(customCardData.images)}
                contentCard={customCardData}
                cardResponsiveWidths={responsiveWidths}
                cardResponsiveHeights={responsiveHeights}
                cardResponsiveBorderRadius={{ base: "16px" }}
                isHidden={isHidden}
                emblaApi={emblaApi}
                index={index}
                sectionIndex={sectionIndex}
                section={section}
                style={
                  isRoadblock
                    ? {
                        flexShrink: 0,
                        maxWidth: isMobile ? maxMobileWidth : "100%",
                        maxHeight: isMobile ? maxMobileHeight : undefined,
                      }
                    : undefined
                }
              />

              <FeatureCardContent
                isHidden={isHidden}
                contentCard={customCardData}
                isRoadblock={isRoadblock}
                index={index}
                sectionIndex={sectionIndex}
                section={section}
                cardResponsiveWidths={
                  isRoadblock ? "100%" : featureCardResponsiveWidths
                }
                style={
                  isRoadblock
                    ? {
                        maxWidth: isMobile ? maxMobileWidth : undefined,
                        maxHeight: isMobile ? maxMobileHeight : undefined,
                        alignSelf: "center",
                      }
                    : undefined
                }
              />
            </Flex>
          );
        }),
      [
        data,
        isMobile,
        selectedIndex,
        isRoadblock,
        responsiveWidths,
        responsiveHeights,
        section,
        sectionIndex,
      ],
    );

    return isRoadblock ? (
      <EmblaCarousel
        slides={renderSlides}
        slideType="featureRoadblock"
        options={OPTIONS}
        onSlideChange={handleSlideChange}
      />
    ) : (
      <Flex role="group">
        <ButtonCarousel
          width="100%"
          slidesToScroll="auto"
          containerProps={{
            gap: isMobile ? 4 : 8,
            pl: isMobile ? 4 : undefined,
          }}
          includeSpacer
          includeButtons
          showScrollbar={isMobile}
          big
        >
          {renderSlides(undefined)}
        </ButtonCarousel>
      </Flex>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if data length, scrollByCount, isRoadblock, or sectionIndex change
    // Comparing data array by reference is fine since it comes from API
    return (
      prevProps.data === nextProps.data &&
      prevProps.isRoadblock === nextProps.isRoadblock &&
      prevProps.sectionIndex === nextProps.sectionIndex
    );
  },
);

FeatureCardContainer.displayName = "FeatureCardContainer";

function getCoverImage(images: Image[]): string {
  let heroImage = images.find(
    (i) => i.image_type_id === FEATURE_HERO_CARD_TYPE_3_2,
  );
  if (heroImage) {
    return heroImage.small_url;
  } else {
    return images[0]?.small_url; // 3:2 - 864x576
  }
}
